import { getAuthorized } from "@/lib/fetch";
import { useLocalStorage } from "@/lib/storage";
import { Button } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type Props = {
  language?: "fr" | "en";
};

const getBrowserLanguage = (): "fr" | "en" => {
  const lang = navigator.language.toLowerCase();
  return lang.startsWith("fr") ? "fr" : "en";
};

const fetchPrivacyContent = async (language: string) => {
  const response = await getAuthorized(
    `/privacy/privacy-${language}.md`,
    undefined,
    false,
  );
  return response.text();
};

export default function PrivacyPolicy({ language: propLanguage }: Props) {
  const [language, setLanguage] = useLocalStorage<"fr" | "en">(
    "lang",
    propLanguage || getBrowserLanguage(),
  );

  const {
    data: content = "",
    isLoading,
    error,
  } = useQuery({
    queryKey: ["privacy-content", language],
    queryFn: () => fetchPrivacyContent(language),
    retry: false,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "fr" ? "en" : "fr"));
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse h-screen bg-gray-100 dark:bg-gray-800 rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-red-500">
        Une erreur est survenue lors du chargement de la politique de
        confidentialité.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-end mb-4">
        <Button
          color="primary"
          radius="full"
          variant="flat"
          onPress={toggleLanguage}
          className="text-sm"
        >
          {language === "fr" ? "English" : "Français"}
        </Button>
      </div>
      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
    </div>
  );
}
